import moment from 'moment'
import { createSelector } from 'reselect'

import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { getFlightItems } from 'checkout/selectors/view/flights'
import { getBedbankItems, getLeHotelItems } from 'checkout/selectors/view/hotels'
import { getVillaItems } from 'checkout/selectors/view/villa'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { queryKeyFareClass } from 'constants/url'
import {
  OfferPageRoom,
  OfferPageState,
  generateOfferPageRoom,
  initialOfferPageRooms,
} from 'contexts/OfferPage/offerPageStateReducer'
import { arrayToObject } from 'lib/array/arrayUtils'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { airportLocationToAirport } from 'lib/flights/flightUtils'
import { getLowestPricePackage } from 'lib/offer/getLowestPricePackage'
import { decodeOfferPageParams } from 'lib/offer/offerPageUtil'
import { isBundleOffer, isFlashHotelOffer, isHotelPackage, isLEHotel } from 'lib/offer/offerTypes'
import {
  getBedbankAndFlightsChildMaxAge,
  getOfferAndFlightsMaxChildAge,
  getPackageUniqueKey,
} from 'lib/offer/offerUtils'
import { getIsMultiRoomEnabled } from 'selectors/featuresSelectors'
import { getDefaultAirport } from 'selectors/flightsSelectors'

import { getPromotionMinLos } from './bedbankOfferPageSelectors'
import { FlightsClassTypes } from 'constants/flight'

const getInitialLEHotelStateFromCheckout = createSelector(
  (state: App.State) => getLeHotelItems(state),
  (state: App.State) => getVillaItems(state),
  (_state: App.State, offer: App.Offer) => offer,
  (state: App.State, offer: App.Offer) => isLEHotel(offer) && getIsMultiRoomEnabled(state, offer),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (accommodationItems, villaItems, offer, isMultiRoomEnabled, connectedRoomOfferIds): Partial<OfferPageState> => {
    const items = [
      ...accommodationItems.filter(isInstantBookingLEHotelItem),
      ...villaItems,
    ].filter(item => item.offerId === offer.id)

    if (items.length === 0) { return {} }

    const packagesByKey = arrayToObject(offer.packages, pkg => getPackageUniqueKey(pkg.id, pkg.duration, pkg.roomRate!.id))

    const rooms = items.reduce<Array<OfferPageRoom>>(
      (accumulator, item) => {
        const pkg = packagesByKey[getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)]
        if (!pkg) {
          return accumulator
        }

        if (isMultiRoomEnabled) {
          // make sure our room ids match on the package and the room
          if (pkg.roomRate) {
            const room = generateOfferPageRoom(item.occupancy, {
              packageOption: isHotelPackage(pkg) ? pkg : undefined,
            })

            accumulator.push(room)
          }
        } else {
          // no multi room, only need rooms
          accumulator.push(generateOfferPageRoom(item.occupancy))
        }
        return accumulator
      }, [])

    return {
      checkInDate: items[0].checkIn,
      checkOutDate: items[0].checkOut,
      duration: items[0].duration,
      durationSelected: true,
      rooms,
      isMultiRoomConnected: !!connectedRoomOfferIds?.length,
    }
  },
)

export const getCharterBundleRoute = createSelector(
  (_state: App.State, offer: App.AnyOffer | undefined) => offer,
  (state: App.State) => state.geo.currentRegionCode,
  (offer, regionCode): App.CharterBundleRoute | undefined => {
    // Determine if charter is enabled based on brand/region
    const isMaldivesCharterEnabled = regionCode === 'AU'

    if (
      isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      isMaldivesCharterEnabled
    ) {
      if (offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)) {
        return MALDIVES_CHARTER_ROUTE
      }
    }

    return undefined
  },
)

/**
 * Returns true if the offer is a Maldives charter flash offer but the feature flag is disabled.
 * Used to show 404 for charter offers when the feature is not enabled for the brand/user.
 */
export const getShouldBlockMaldivesCharterOffer = createSelector(
  (_state: App.State, offer: App.AnyOffer | undefined) => offer,
  (state: App.State) => state.geo.currentRegionCode,
  (offer, regionCode): boolean => {
    const isMaldivesCharterOffer = isFlashHotelOffer(offer) &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

    // Determine if charter is enabled based on brand/region
    const isMaldivesCharterEnabled = regionCode === 'AU'

    return isMaldivesCharterOffer && !isMaldivesCharterEnabled
  },
)

const getInitialPropertyHotelState = createSelector(
  getInitialLEHotelStateFromCheckout,
  (state: App.State) => state.system.features.flags[OptimizelyFeatureFlags.sppUseAccomContent],
  (_state: App.State, _offer: App.Offer, property: App.Property.Property) => property,
  (_state: App.State, offer: App.Offer) => offer,
  (state: App.State, offer: App.Offer) => getCharterBundleRoute(state, offer),
  (accommodationState, shouldUseAccomContent, property, offer, charterBundleRoute): Partial<OfferPageState> => {
    return {
      duration: shouldUseAccomContent ? property.marketedLos : offer.lowestPricePackage?.duration,
      charterBundleRoute,
      ...accommodationState,
    }
  },
)

const getInitialLEHotelState = createSelector(
  getInitialLEHotelStateFromCheckout,
  (_state: App.State, offer: App.Offer) => offer,
  (state: App.State, offer: App.Offer) => getCharterBundleRoute(state, offer),
  (accommodationState, offer, charterBundleRoute): Partial<OfferPageState> => {
    return {
      duration: offer.lowestPricePackage?.duration,
      charterBundleRoute,
      ...accommodationState,
    }
  },
)

const getInitialBedbankState = createSelector(
  (state: App.State) => getBedbankItems(state),
  (items): Partial<OfferPageState> => {
    if (items.length === 0) {
      return {}
    }
    const referenceItem = items[0]
    const rooms = items.map(item => generateOfferPageRoom({ ...item.occupancy, roomId: item.roomId, rateId: item.roomRateId }))

    return {
      ...('checkIn' in referenceItem && { checkInDate: referenceItem.checkIn }),
      ...('checkOut' in referenceItem && { checkOutDate: referenceItem.checkOut }),
      rooms,
    }
  },
)

const getOfferPageAirports = createSelector(
  (state: App.State) => state.geo.originAirports,
  (state: App.State, offer: App.Offer | undefined) => getCharterBundleRoute(state, offer),
  (originAirports, charterBundleRoute): Array<App.Airport> => {
    if (charterBundleRoute) {
      const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]

      return [
        routeInfo.originAirport,
        ...routeInfo.feederFlightOrigins,
      ].map(airportLocationToAirport)
    }

    return originAirports
  },
)

const getOfferPageDefaultAirport = createSelector(
  (state: App.State) => getDefaultAirport(state),
  (state: App.State, offer: App.Offer | undefined) => getCharterBundleRoute(state, offer),
  (defaultAirport, charterBundleRoute): App.Airport | undefined => {
    if (defaultAirport && charterBundleRoute && defaultAirport.countryCode === 'AU') {
      return defaultAirport
    }

    if (charterBundleRoute) {
      const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]
      return airportLocationToAirport(routeInfo.originAirport)
    }

    return defaultAirport
  },
)

const getInitialFlightState = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State, offer: App.Offer | undefined) => getOfferPageAirports(state, offer),
  (state: App.State, offer: App.Offer | undefined) => getOfferPageDefaultAirport(state, offer),
  (state: App.State) => state.flights.journeys,
  (state: App.State, offer: App.Offer | undefined) => offer,
  (state: App.State, offer: App.Offer | undefined) => getCharterBundleRoute(state, offer),
  (state: App.State) => state.router.location.search,
  (state: App.State) => state.geo.originAirports,
  (items, airportList, defaultAirport, journeys, offer, charterBundleRoute, searchParams, allOriginAirports): Partial<OfferPageState> => {
    let flightsSelected = false
    if (
      (isLEHotel(offer) && offer.bundledWithFlightsOnly) ||
      charterBundleRoute
    ) {
      flightsSelected = true
    }

    let selectedAirport = defaultAirport

    const urlSearchParams = decodeOfferPageParams(searchParams)
    if (urlSearchParams.departureAirportCode) {
      const airportFromUrl = allOriginAirports.find(airport => airport.code === urlSearchParams.departureAirportCode)
      if (airportFromUrl) {
        selectedAirport = airportFromUrl
      }
    }

    // Assumption: Only one unique flight
    const referenceItem = items.at(0)
    const departureJourney = journeys?.find(journey => journey.id === referenceItem?.searchId)
    const airportCodeFromJourney = departureJourney?.departing.flights[0].departingAirport
    if (airportCodeFromJourney) {
      selectedAirport = airportList.find(airport => airport.code === airportCodeFromJourney) ?? selectedAirport
    }

    if (charterBundleRoute && !urlSearchParams.departureAirportCode) {
      const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]
      const selectedAirportCode = selectedAirport?.code
      if (!selectedAirportCode || !(selectedAirportCode === routeInfo.originAirport.airportCode || routeInfo.feederFlightOrigins.some(airport => airport.airportCode === selectedAirportCode))) {
        // Selected airport isn't a valid origin for this charter flight. Revert to the charter origin airport.
        selectedAirport = airportList.find(airport => airport.code === routeInfo.originAirport.airportCode)
      }
    }

    return {
      airport: selectedAirport,
      flights: flightsSelected,
    }
  },
)

const getInitialSearchState = createSelector(
  (state: App.State) => state.router.location.search,
  (state: App.State, offer: App.Offer | App.BedbankOffer | App.BundleOffer) => getCharterBundleRoute(state, offer),
  (_state: App.State, offer: App.Offer | App.BedbankOffer | App.BundleOffer) => offer,
  (rawSearchParams, charterBundleRoute, offer) => {
    const args = isLEHotel(offer) || isBundleOffer(offer) ?
        [getOfferAndFlightsMaxChildAge(offer as App.Offer), (offer as App.HotelOffer).property.maxInfantAge, (offer as App.HotelOffer).property.maxTeenagerAge] :
        [getBedbankAndFlightsChildMaxAge(offer as App.BedbankOffer)]

    const searchParams = decodeOfferPageParams(
      rawSearchParams,
      ...args,
    )

    const result: Partial<OfferPageState> = {
      ...(searchParams.rooms.length > 0 && { rooms: searchParams.rooms.map(room => generateOfferPageRoom(room)) }),
      ...(searchParams.multiRoomMode && { multiRoomMode: searchParams.multiRoomMode }),
      priceMode: searchParams.priceMode ?? (charterBundleRoute ? 'perPerson' : 'total'),
    }

    if (searchParams.dates.checkIn) {
      const checkInDate = searchParams.dates.checkIn
      const checkInMomentMaybe = moment(checkInDate)
      const checkInMoment = checkInMomentMaybe.isValid() ? checkInMomentMaybe : undefined
      let availableCheckInDate = !!checkInMoment
      if (isLEHotel(offer) && offer.bookByDate && offer.travelToDate && checkInMoment) {
        // Must be booking before/on the book by date
        const bookByDate = moment(offer.bookByDate)
        const travelToDate = moment.utc(offer.travelToDate)
        if (moment().isSameOrAfter(bookByDate.add(1, 'day')) || checkInMoment.isSameOrAfter(travelToDate)) {
          availableCheckInDate = false
        }
      }

      if (availableCheckInDate) {
        result.checkInDate = checkInDate

        if (searchParams.dates.checkOut) {
          const checkOutMomentMaybe = moment(searchParams.dates.checkOut)
          const checkOutMoment = checkOutMomentMaybe.isValid() ? checkOutMomentMaybe : undefined
          result.checkOutDate = checkOutMoment ? searchParams.dates.checkOut : undefined
          if (checkOutMoment) {
            result.duration = Math.round(moment.duration(checkOutMoment.diff(result.checkInDate)).asDays())
            result.durationSelected = true
          }
        }
      }
    }

    if (searchParams.bundledOfferId && searchParams.dates.checkIn && searchParams.dates.checkOut) {
      result.bundledOffers = {
        [searchParams.bundledOfferId]: {
          checkInDate: result.checkInDate,
          checkOutDate: result.checkOutDate,
          duration: result.duration,
        },
      }
    }

    if (searchParams.duration && !result.duration) {
      result.duration = searchParams.duration
    }

    result.fareClass = FlightsClassTypes.ECONOMY
    if (charterBundleRoute && searchParams[queryKeyFareClass]) {
      const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]
      if (routeInfo?.fareClassOptions.some(fareClass => fareClass.value === searchParams[queryKeyFareClass])) {
        result.fareClass = searchParams[queryKeyFareClass]
      }
    }

    return result
  },
)

export const getInitialPropertyPageState = createSelector(
  (state: App.State, lpcOffer: App.HotelOffer | undefined, _bedbankOffer: App.BedbankOffer | undefined, property: App.Property.Property) =>
    lpcOffer ? getInitialPropertyHotelState(state, lpcOffer, property) : {},
  (state: App.State, _lpcOffer: App.HotelOffer | undefined, bedbankOffer: App.BedbankOffer | undefined, property: App.Property.Property) =>
    bedbankOffer ? { ...getInitialBedbankState(state), duration: property.marketedLos } : {},
  (state: App.State, lpcOffer: App.HotelOffer | undefined, bedbankOffer: App.BedbankOffer | undefined) =>
    getInitialSearchState(state, (lpcOffer ?? bedbankOffer)!),
  (state: App.State, lpcOffer: App.HotelOffer | undefined) => getInitialFlightState(state, lpcOffer),
  (accommodationState, bedbankState, searchState, flightState): Partial<OfferPageState> => {
    // The order of the spread elements matter!
    return {
      ...accommodationState,
      ...bedbankState,
      ...searchState,
      ...flightState,
      // NB: We ignore bedbank promotion minimum LoS, as we don't use SPP if the bedbank offer has
      // promotions.
    }
  },
)

export const getInitialLeOfferPageState = createSelector(
  (state: App.State, offer: App.Offer) => getInitialLEHotelState(state, offer),
  (state: App.State, offer: App.Offer) => getInitialSearchState(state, offer),
  (state: App.State, offer: App.Offer) => getInitialFlightState(state, offer),
  (accommodationState, searchState, flightState): Partial<OfferPageState> => {
    // The order of the spread elements matter!
    return {
      ...accommodationState,
      ...searchState,
      ...flightState,
    }
  },
)

export const getInitialBedbankOfferPageState = createSelector(
  (state: App.State, _offer: App.BedbankOffer) => getInitialBedbankState(state),
  (state: App.State, offer: App.BedbankOffer) => getInitialSearchState(state, offer),
  (_state: App.State, offer: App.BedbankOffer) => getPromotionMinLos(offer),
  (state: App.State) => getInitialFlightState(state, undefined),
  (bedbankState, searchState, promotionMinLos, flightState) => {
    // The order of the spread elements matter! It certainly does!
    return {
      ...bedbankState,
      ...searchState,
      ...flightState,
      duration: searchState.duration ? searchState.duration : promotionMinLos,
    }
  },
)

export const getInitialBundleAndSaveOfferPageState = createSelector(
  (_state: App.State, offer: App.BundleOffer) => offer,
  (state: App.State, offer: App.BundleOffer) => getInitialSearchState(state, offer),
  (offer, searchState): Partial<OfferPageState> => {
    const bundledOffers = Object.values(offer.bundledOffers)
    const lowestPriceBundlePackage = offer.lowestPriceBundlePackage!

    const allOffersIds = Object.keys(offer.bundledOffers)

    const currentOfferId = allOffersIds[0]

    return {
      paginator: {
        currentOfferId,
        nextOfferId: allOffersIds[allOffersIds.indexOf(currentOfferId) + 1],
        backOfferId: allOffersIds[allOffersIds.indexOf(currentOfferId) - 1],
      },
      duration: lowestPriceBundlePackage.duration,
      bundleOffer: isBundleOffer(offer) ? offer : undefined,
      offer,
      bundledOffers: arrayToObject(bundledOffers,
        bundleOffer => bundleOffer.id,
        bundleOffer => {
          const bundlePackages = offer.packages.filter((p) => p.offerId === bundleOffer.id)
          const pkg = lowestPriceBundlePackage.packages.find((pkg) => pkg.offerId === bundleOffer.id)!
          const packages = bundlePackages.length ? bundlePackages : [pkg]
          const attachedOffer: App.HotelOffer = {
            ...offer,
            ...bundleOffer,
            packages,
            lowestPricePackage: getLowestPricePackage(offer, packages),
            loyalty: {
              hasPackageTypeUpgrades: false,
              hasRoomTypeUpgrades: false,
            },
          }

          return {
            offer: attachedOffer,
            duration: pkg.duration,
          }
        },
      ),
      rooms: searchState.rooms?.length ? [searchState.rooms[0]] : initialOfferPageRooms,
    }
  },
)
